import React, { useState, useMemo } from "react";
import {
  Typography,
  Grid,
  Box,
  Divider,
  Popover,
  Button,
  Badge,
} from "@material-ui/core";
import { deepOrange, grey, blueGrey, green } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import { InventoryStatus } from "../../../utils/constants";
import {
  getLocationFullName,
  getLocationViewName,
  isLocationAvailable,
} from "../../../utils/locationUtils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import { getInventoryQuantity } from "../../../utils/utils";
import { useWorkOrderContext } from "./context";
import { CloseIcon } from "../../../components/common/iconography";

const useStyles = makeStyles((theme) => ({
  mapContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
    minHeight: theme.spacing(24),
    width: "100%",
  },
  backdrop: {
    zIndex: 1001,
  },
  popvover: {
    padding: theme.spacing(1),
  },
  mapContainerMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
    minHeight: theme.spacing(24),
    width: "100%",
  },
  bayContainer: {
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "center",
    alignContent: "space-between",
    border: "1px solid #b0bec5",
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    paddingTop: theme.spacing(1),
  },
  levelContainer: {
    border: "1px solid",
    borderColor: blueGrey[500],
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  locationAvatar: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(
      theme.palette.type === "dark" ? grey[400] : grey[300],
    ),
    backgroundColor: theme.palette.type === "dark" ? grey[400] : grey[300],
    borderColor: theme.palette.type === "dark" ? grey[400] : grey[300],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    width: "100%",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  locationUsedAvatar: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(
      theme.palette.type === "dark" ? grey[400] : grey[300],
    ),
    backgroundColor: theme.palette.type === "dark" ? grey[400] : grey[300],
    borderColor:
      theme.palette.type === "dark" ? deepOrange[500] : deepOrange[500],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    width: "100%",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  locationUsedAvailableAvatar: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(green[600]),
    backgroundColor: theme.palette.type === "dark" ? green[600] : green[600],
    borderColor:
      theme.palette.type === "dark" ? deepOrange[500] : deepOrange[500],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    width: "100%",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  selectedLocationAvatar: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
    borderColor: deepOrange[500],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    width: "100%",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  locationAvailableAvatar: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(green[600]),
    backgroundColor: green[600],
    borderColor: green[600],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    width: "100%",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  redCircle: {
    position: "absolute",
    top: -theme.spacing(1.25),
    left: -theme.spacing(1.25),
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderStyle: "solid",
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    borderRadius: theme.spacing(1.5),
    backgroundColor: deepOrange[500],
    color: theme.palette.getContrastText(deepOrange[600]),
  },
  badge: {
    "&:hover": {
      cursor: "pointer",
    },
  },
}));

export const StructuredInboundAisle = ({ handleLocation }) => {
  const { state, dispatch } = useWorkOrderContext();
  const {
    currentInventory,
    selectedLocation,
    disableSelection,
    selectedAisle,
    bayList,
  } = state;
  const classes = useStyles();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSelectLocation = (location, evt) => {
    if (disableSelection) return;
    evt.preventDefault();
    evt.stopPropagation();
    setAnchorEl(null);
    const payload = location.id === selectedLocation?.id ? null : location;
    if (handleLocation) {
      dispatch({ type: "SET_SELECTED_LOCATION", payload: location });
      handleLocation(location);
      return;
    } else {
      dispatch({ type: "SET_SELECTED_LOCATION", payload });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getTotalItemsPlaced = () => {
    let total = 0;
    if (currentInventory.inventoryItems?.items.length > 0) {
      total = currentInventory.inventoryItems.items.reduce(
        (total, item) =>
          item.status === InventoryStatus.UNLOADED
            ? (total += item.quantityOnHand)
            : total,
        0,
      );
    }
    const uom = getUnitName(currentInventory.inventoryUOM);
    const text = `${total} ${uom}`;
    return text;
  };

  const getTotalItemsInLocation = () => {
    let totalInAisle = 0;
    if (currentInventory.inventoryItems?.items.length > 0) {
      totalInAisle = currentInventory.inventoryItems.items.reduce(
        (total, item) =>
          item.locationId === selectedLocation?.id &&
          item.status === InventoryStatus.UNLOADED
            ? (total += item.quantityOnHand)
            : total,
        0,
      );
    }
    const totalInAisleUom = getUnitName(currentInventory.inventoryUOM);
    return `${totalInAisle} ${totalInAisleUom}`;
  };

  const getTotalQuantityText = () => {
    return (
      <div
        className={classes.popvover}
        style={!isSmall ? { width: "30rem" } : null}
      >
        <Grid container>
          <Grid item xs={12}>
            <Box
              mb={1}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography component="span">
                Location {getLocationFullName(selectedLocation)}
              </Typography>
              <CloseIcon onClick={handleClose} />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box mb={1}>
              <Divider />
            </Box>
          </Grid>
          <Grid item xs={6}>{`Total ${
            !isSmall ? "inventory items " : ""
          }placed`}</Grid>
          <Grid item xs={6}>
            {getTotalItemsPlaced()}
          </Grid>
          <Grid item xs={6}>{`Total ${
            !isSmall ? `placed ` : ""
          }in ${getLocationFullName(selectedLocation)}`}</Grid>
          <Grid item xs={6}>
            {getTotalItemsInLocation()}
          </Grid>
          <Grid item xs={6}>
            Capacity used
          </Grid>
          <Grid item xs={6}>
            {selectedLocation.capacityUsed}%
          </Grid>
          {selectedLocation.capacityAllocated > 0 && (
            <>
              <Grid item xs={6}>
                Capacity allocated
              </Grid>
              <Grid item xs={6}>
                {selectedLocation.capacityAllocated}%
              </Grid>
            </>
          )}
          {selectedLocation?.productId && (
            <>
              <Grid item xs={6}>
                Account
              </Grid>
              <Grid item xs={6}>
                {selectedLocation.accountName}
              </Grid>
              <Grid item xs={6}>
                Product
              </Grid>
              <Grid item xs={6}>
                {selectedLocation.productName}
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Box
              display="flex"
              mt={0}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button onClick={handleClose}>Close</Button>
            </Box>
          </Grid>
        </Grid>
      </div>
    );
  };

  const handleInfoClick = (evt) => {
    if (!selectedLocation) return;
    evt.preventDefault();
    evt.stopPropagation();
    setAnchorEl(evt.currentTarget);
  };

  if (!selectedAisle?.id || bayList?.length === 0) return <></>;
  const open = Boolean(anchorEl);

  return (
    <>
      <Grid item xs={12}>
        <div
          className={
            isSmall ? classes.mapContainerMobile : classes.mapContainer
          }
        >
          {selectedLocation && (
            <Popover
              id="location-popover"
              open={open}
              onClose={handleClose}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
            >
              <Box p={1}>{getTotalQuantityText()}</Box>
            </Popover>
          )}
          {bayList.map((bay, bayIdx) => (
            <div className={classes.bayContainer} key={bayIdx}>
              <Box m={1}>
                <Typography>{`Bay ${
                  (bayIdx + 1 < 10 ? " 0" : " ") + (bayIdx + 1)
                }`}</Typography>
              </Box>
              <Divider />
              {bay.map((level, idx) => (
                <div className={classes.levelContainer} key={idx}>
                  {level.map((location) => (
                    <Location
                      key={location.id}
                      isSmall={isSmall}
                      location={location}
                      classes={classes}
                      handleInfoClick={handleInfoClick}
                      handleSelectLocation={handleSelectLocation}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </Grid>
    </>
  );
};

const Location = ({
  isSmall,
  location,
  classes,
  handleInfoClick,
  handleSelectLocation,
}) => {
  const { state } = useWorkOrderContext();
  const {
    selectedLocation,
    currentInventory,
    disableSelection,
    showInventoryQuantity,
    searchInventoryItemsMap,
    selectedAisle,
    bayList,
  } = state;

  const avatarClassName = useMemo(() => {
    if (location.bay === 2 && location.level === 1 && location.position === 1) {
      debugger;
    }
    if (selectedLocation?.id === location.id) {
      return classes.selectedLocationAvatar;
    }
    if (disableSelection) return classes.locationAvatar;
    let available = isLocationAvailable(bayList, location, currentInventory);

    const sameInventory = currentInventory.inventoryItems?.items?.find(
      (itm) => itm.locationId === location.id,
    );
    if (available) {
      if (sameInventory) return classes.locationUsedAvailableAvatar;
      return classes.locationAvailableAvatar;
    }
    if (sameInventory) return classes.locationUsedAvatar;
    return classes.locationAvatar;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.id, selectedLocation?.id, bayList]);

  const getFullName = (location, size = 16) => {
    if (!location) return "";
    const name = getLocationViewName(location);
    if (!isSmall) return name;
    if (name.length <= size) return name;
    return name.substring(0, size - 3) + "...";
  };

  const getCapcityUsedText = (location) => {
    if (
      !showInventoryQuantity ||
      (!selectedLocation && selectedAisle?.locationLoad.other.selected) ||
      selectedLocation?.locationLoad.other.selected
    ) {
      return `${location.capacityUsed ? location.capacityUsed : 0}%`;
    } else {
      const inventoryLoadType =
        currentInventory.reloadYardCommodity?.commodity?.locationLoadType;
      if (
        !inventoryLoadType ||
        inventoryLoadType === "other" ||
        inventoryLoadType === "container"
      )
        return "";
      return getInventoryQuantity(location, currentInventory).name;
    }
  };

  return selectedLocation === location ? (
    <Badge
      onClick={handleInfoClick}
      color="primary"
      badgeContent={<Typography>?</Typography>}
      key={location.id}
      className={classes.badge}
    >
      <div
        className={avatarClassName}
        onClick={(evt) => handleSelectLocation(location, evt)}
      >
        <Typography align="center" variant="body2">
          {getFullName(location)}
        </Typography>
        <Typography align="center" variant="body2">
          {getCapcityUsedText(location)}
        </Typography>
        {searchInventoryItemsMap[location.id] && (
          <Box className={classes.redCircle}>
            {searchInventoryItemsMap[location.id].length}
          </Box>
        )}
      </div>
    </Badge>
  ) : (
    <div
      key={location.id}
      className={avatarClassName}
      onClick={(evt) => handleSelectLocation(location, evt)}
    >
      <Typography align="center" variant="body2">
        {getFullName(location)}
      </Typography>
      <Typography align="center" variant="body2">
        {getCapcityUsedText(location)}
      </Typography>
      {searchInventoryItemsMap[location.id] && (
        <Box className={classes.redCircle}>
          {searchInventoryItemsMap[location.id].length}
        </Box>
      )}
    </div>
  );
};
